import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Drivers } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class DriverPreferencesService {
  private STORAGE_KEY = 'driver_display_preferences';
  
  // BehaviorSubject to track selected driver IDs
  private selectedDriverIdsSubject = new BehaviorSubject<number[]>([]);
  public selectedDriverIds$ = this.selectedDriverIdsSubject.asObservable();

  constructor() {
    this.loadPreferences();
  }

  /**
   * Load saved preferences from localStorage
   */
  private loadPreferences(): void {
    const savedPreferences = localStorage.getItem(this.STORAGE_KEY);
    console.log('Loading saved preferences from localStorage:', savedPreferences);
    
    if (savedPreferences) {
      try {
        const driverIds = JSON.parse(savedPreferences);
        this.selectedDriverIdsSubject.next(driverIds);
      } catch (e) {
        this.selectedDriverIdsSubject.next([]);
      }
    } else {
      // If no preferences are saved, default to an empty array
      this.selectedDriverIdsSubject.next([]);
    }
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(driverIds: number[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(driverIds));
    this.selectedDriverIdsSubject.next(driverIds);
  }

  /**
   * Get the current selected driver IDs
   */
  getSelectedDriverIds(): number[] {
    return this.selectedDriverIdsSubject.getValue();
  }

  /**
   * Update the selected driver IDs
   */
  updateSelectedDrivers(driverIds: number[]): void {
    this.savePreferences(driverIds);
  }

  /**
   * Select all drivers of a specific type
   */
  selectDriversByType(drivers: Drivers[], type: 'Pneumatic' | 'Dump' | 'Hopper' | 'Conveyor'): void {
    const currentSelection = this.getSelectedDriverIds();
    const driversOfType = drivers.filter(driver => driver.type === type);
    const driverIdsOfType = driversOfType.map(driver => driver.driver_id);
    
    // Add all drivers of the specified type that aren't already selected
    const newSelection = [...new Set([...currentSelection, ...driverIdsOfType])];
    this.savePreferences(newSelection);
  }

  /**
   * Deselect all drivers of a specific type
   */
  deselectDriversByType(drivers: Drivers[], type: 'Pneumatic' | 'Dump' | 'Hopper' | 'Conveyor'): void {
    const currentSelection = this.getSelectedDriverIds();
    const driversOfType = drivers.filter(driver => driver.type === type);
    const driverIdsOfType = driversOfType.map(driver => driver.driver_id);
    
    // Remove all drivers of the specified type from the selection
    const newSelection = currentSelection.filter(id => !driverIdsOfType.includes(id));
    this.savePreferences(newSelection);
  }

  /**
   * Toggle selection of a single driver
   */
  toggleDriver(driverId: number): void {
    const currentSelection = this.getSelectedDriverIds();
    if (currentSelection.includes(driverId)) {
      // Remove driver if already selected
      this.savePreferences(currentSelection.filter(id => id !== driverId));
    } else {
      // Add driver if not selected
      this.savePreferences([...currentSelection, driverId]);
    }
  }

  /**
   * Check if a driver is selected
   */
  isDriverSelected(driverId: number): boolean {
    return this.getSelectedDriverIds().includes(driverId);
  }

  /**
   * Select all drivers
   */
  selectAllDrivers(drivers: Drivers[]): void {
    const allDriverIds = drivers.map(driver => driver.driver_id);
    this.savePreferences(allDriverIds);
  }

  /**
   * Deselect all drivers
   */
  deselectAllDrivers(): void {
    this.savePreferences([]);
  }
}
